// Pure helpers for the pen-reading capture flow (issue 3). Kept free of React
// and DOM so they're unit-testable under `bun test` (which has no jsdom): the
// ReadingCaptureCard holds the state and renders; these do the unit math.
//
// A "pending reading" mirrors the editable fields a grower sees: each metric is
// a string in the user's DISPLAY units (pH unitless, EC in ecUnit, temp in
// temperatureUnit, level in levelUnit). Conversion to storage units (mS/cm, °C,
// gallons) happens once, on save, in pendingToCreateInput.

import type { LiveSamplePayload } from "@/lib/livestream/client";
import type { CreateGrowReadingInput } from "@/lib/api/types";
import {
  ecFromMsCm,
  ecToMsCm,
  levelToGal,
  tempFromCelsius,
  tempToCelsius,
  type ECUnit,
  type LevelUnit,
  type ReservoirFactors,
  type TemperatureUnit,
} from "@/lib/units";

// A reading being entered/edited before it's saved or discarded. Values are
// display-unit strings; source/capturedAt track a pen capture (so a saved
// reading sorts by the pen sample's own time and is tagged "pen").
export interface PendingReading {
  ph: string;
  ec: string;
  temp: string;
  level: string;
  notes: string;
  source: "manual" | "pen";
  capturedAt: string | null;
}

// Display units needed to render/format a pen sample into the fields.
export interface DisplayUnits {
  ecUnit: ECUnit;
  temperatureUnit: TemperatureUnit;
}

// Everything needed to convert a pending reading back to storage units on save
// (level needs the bound reservoir's geometry; gallons-only when absent).
export interface SaveUnits extends DisplayUnits {
  levelUnit: LevelUnit;
  factors: ReservoirFactors;
}

/** An empty pending reading (nothing captured yet). */
export const EMPTY_PENDING: PendingReading = {
  ph: "",
  ec: "",
  temp: "",
  level: "",
  notes: "",
  source: "manual",
  capturedAt: null,
};

/** Parse a display-field string to a number, or undefined when blank/NaN. */
export function numOrUndef(v: string): number | undefined {
  const t = v.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Snapshot a live pen sample into a pending reading (in display units), tagged
 * source="pen" with the sample's own timestamp. pH prefers the filtered value.
 * When `prev` is supplied (pressing `c` again to re-snapshot), the manually
 * entered level + notes are preserved — the pen measures chemistry, not volume
 * — and any metric the sample is missing keeps its previous value.
 */
export function snapshotFromSample(
  sample: LiveSamplePayload,
  receivedAt: number,
  units: DisplayUnits,
  prev?: PendingReading | null,
): PendingReading {
  const p = sample.ph_filtered ?? sample.ph;
  return {
    ph: typeof p === "number" ? p.toFixed(2) : (prev?.ph ?? ""),
    ec:
      typeof sample.ec_ms_per_cm === "number"
        ? ecFromMsCm(sample.ec_ms_per_cm, units.ecUnit).toFixed(
            units.ecUnit === "ppm700" ? 0 : 2,
          )
        : (prev?.ec ?? ""),
    temp:
      typeof sample.temperature_c === "number"
        ? tempFromCelsius(sample.temperature_c, units.temperatureUnit).toFixed(1)
        : (prev?.temp ?? ""),
    level: prev?.level ?? "",
    notes: prev?.notes ?? "",
    source: "pen",
    capturedAt: new Date(receivedAt).toISOString(),
  };
}

/**
 * Convert a pending reading to the storage-unit POST body. Blank fields become
 * undefined (not sent); EC/temp/level convert from display units; captured_at +
 * source ride along only for a pen capture (a manual reading defaults to now()
 * server-side).
 */
export function pendingToCreateInput(
  pending: PendingReading,
  id: string,
  units: SaveUnits,
): CreateGrowReadingInput {
  const ec = numOrUndef(pending.ec);
  const temp = numOrUndef(pending.temp);
  const level = numOrUndef(pending.level);
  const notes = pending.notes.trim();
  return {
    id,
    ph: numOrUndef(pending.ph),
    ec_ms_per_cm: ec === undefined ? undefined : ecToMsCm(ec, units.ecUnit),
    temp_c: temp === undefined ? undefined : tempToCelsius(temp, units.temperatureUnit),
    level_gal:
      level === undefined ? undefined : levelToGal(level, units.levelUnit, units.factors),
    source: pending.source,
    notes: notes || undefined,
    captured_at: pending.source === "pen" ? (pending.capturedAt ?? undefined) : undefined,
  };
}

/** True when a pending reading has at least one value worth saving. */
export function hasAnyValue(pending: PendingReading): boolean {
  return (
    numOrUndef(pending.ph) !== undefined ||
    numOrUndef(pending.ec) !== undefined ||
    numOrUndef(pending.temp) !== undefined ||
    numOrUndef(pending.level) !== undefined ||
    pending.notes.trim() !== ""
  );
}
